import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

const severityRank = { high: 0, medium: 1, low: 2 };

function ProductPairing({ pairing, index }) {
  const [open, setOpen] = useState(false);
  const products = Array.isArray(pairing.products) ? pairing.products.filter(Boolean) : [];

  return (
    <div className="border-t border-[#EAE7E3]">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 py-4 text-left text-[#2A324B]">
        <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-[#EFF5EE] text-xs font-semibold text-[#397641]">{index + 1}</span>
        <span className="min-w-0 flex-1 break-words text-sm font-semibold leading-snug">{products.join(' + ') || `Product connection ${index + 1}`}</span>
        <ChevronDown size={17} aria-hidden="true" className={`mt-0.5 flex-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pairing.benefit && <p className="mb-4 ml-9 border-l-2 border-[#C3DBC2] pl-3 text-[13px] leading-relaxed text-[#606A79]">{pairing.benefit}</p>}
    </div>
  );
}

export default function RoutineAnalysisModal({ analysis, error, onClose, onRefresh }) {
  const [showDetail, setShowDetail] = useState(false);
  const a = analysis || {};
  const conflicts = Array.isArray(a.conflicts) ? a.conflicts.filter(item => item && typeof item === 'object').sort((x, y) =>
    (severityRank[x?.severity] ?? 1) - (severityRank[y?.severity] ?? 1)) : [];
  const synergies = Array.isArray(a.synergies) ? a.synergies.filter(item => item && typeof item === 'object') : [];
  const ordering = Array.isArray(a.ordering) ? a.ordering.filter(Boolean) : [];
  const tips = Array.isArray(a.tips) ? a.tips.filter(Boolean) : [];
  const hasSupportingDetail = synergies.length + ordering.length + tips.length > 0;
  const hasConflicts = conflicts.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="routine-analysis-title"
        className="flex max-h-[88dvh] w-full max-w-md flex-col rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#ECE9E5] px-5 pb-4 pt-5">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#FDF6EE] text-[#C8956A]" aria-hidden="true">✦</div>
          <h2 id="routine-analysis-title" className="min-w-0 flex-1 text-base font-bold text-[#2A324B]">How your routine works together</h2>
          <button type="button" onClick={onClose} aria-label="Close routine analysis" className="grid h-11 w-11 flex-none place-items-center rounded-xl text-[#2A324B] hover:bg-[#F5F5F5]"><X size={20} /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-6">
          {error ? <p className="text-sm leading-relaxed text-[#2A324B]/70">{error}</p> : <>
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#967762]">Routine analysis</span>
            <h3 className="mb-2 mt-2 text-[24px] font-semibold leading-tight tracking-tight text-[#341C1C]">
              {hasConflicts ? 'A few things to check' : 'Your routine is working well'}
            </h3>
            {a.summary && <p className="mb-5 text-sm leading-relaxed text-[#606A79]">{a.summary}</p>}

            {hasConflicts ? <div className="mb-5 space-y-3">
              {conflicts.map((conflict, index) => <div key={index} className={`rounded-2xl p-4 ${index === 0 ? 'bg-[#FBEDED]' : 'bg-[#FFF4E5]'}`}>
                <p className={`mb-1 text-xs font-bold uppercase tracking-wider ${index === 0 ? 'text-[#A34848]' : 'text-[#996737]'}`}>
                  {index === 0 ? 'Check first' : 'Also worth checking'}
                </p>
                {Array.isArray(conflict.products) && conflict.products.length > 0 &&
                  <p className="mb-1 break-words text-xs font-semibold text-[#2A324B]/75">{conflict.products.filter(Boolean).join(' + ')}</p>}
                {conflict.issue && <p className="text-sm leading-relaxed text-[#2A324B]">{conflict.issue}</p>}
                {conflict.advice && <p className="mt-2 text-[13px] leading-relaxed text-[#2A324B]"><strong>What to do:</strong> {conflict.advice}</p>}
              </div>)}
            </div> : <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-[#F2F7F1] px-3.5 py-3 text-[13px] leading-relaxed text-[#466C4D]">
              <Check size={18} aria-hidden="true" className="mt-0.5 flex-none" />
              <span>No conflicts flagged in this analysis.</span>
            </div>}

            {hasSupportingDetail && <>
              <button type="button" aria-expanded={showDetail} aria-controls="routine-supporting-detail"
                onClick={() => setShowDetail(!showDetail)}
                className="flex w-full items-center justify-between gap-3 border-y border-[#EAE7E3] py-4 text-left text-sm font-bold text-[#2A324B]">
                <span>{showDetail ? 'Hide supporting detail' : hasConflicts ? 'See full analysis' : 'See why your products work together'}</span>
                <ChevronDown size={17} aria-hidden="true" className={`flex-none transition-transform ${showDetail ? 'rotate-180' : ''}`} />
              </button>
              {showDetail && <div id="routine-supporting-detail" className="pb-2">
                {synergies.length > 0 && <section className="pt-5">
                  <h4 className="mb-2 text-sm font-semibold text-[#2A324B]">What works together</h4>
                  {synergies.map((pairing, index) => <ProductPairing key={index} pairing={pairing} index={index} />)}
                </section>}
                {ordering.length > 0 && <section className="border-t border-[#EAE7E3] py-5">
                  <h4 className="mb-2 text-sm font-semibold text-[#2A324B]">Suggested order</h4>
                  <ol className="space-y-2 pl-5 text-[13px] leading-relaxed text-[#606A79]">
                    {ordering.map((step, index) => <li key={index} className="list-decimal break-words">{step}</li>)}
                  </ol>
                </section>}
                {tips.length > 0 && <section className="border-t border-[#EAE7E3] py-5">
                  <h4 className="mb-2 text-sm font-semibold text-[#2A324B]">Tips</h4>
                  <ul className="space-y-2 pl-5 text-[13px] leading-relaxed text-[#606A79]">
                    {tips.map((tip, index) => <li key={index} className="list-disc break-words">{tip}</li>)}
                  </ul>
                </section>}
              </div>}
            </>}

            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-[#2A324B]/50">
              <span>Saved. Updates when your routine changes.</span>
              {onRefresh && <button type="button" onClick={onRefresh} className="min-h-[44px] flex-none rounded-lg px-2 py-2 font-semibold text-[#A8742F]">Refresh</button>}
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}
